import {
  createFileRoute,
  retainSearchParams,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import type { Permission } from "@/lib/queries/permissions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  permissionActionsQueryOptions,
  permissionResourcesQueryOptions,
  permissionsListQueryOptions,
} from "@/lib/queries/permissions";

import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import { Button } from "@/components/ui/button";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
// import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  search: z.string().optional(),
  resource: z.string().optional().catch(undefined),
  action: z.string().optional().catch(undefined),
  is_active: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/permissions/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [
      stripSearchParams({
        search: "",
        resource: undefined,
        action: undefined,
        is_active: undefined,
      }),
      retainSearchParams(true),
    ],
  },

  loaderDeps: ({ search }) => {
    return search;
  },

  loader({ context: { queryClient }, deps }) {
    [
      permissionsListQueryOptions(deps),
      permissionActionsQueryOptions,
      permissionResourcesQueryOptions,
    ].forEach((opts) => queryClient.ensureQueryData(opts as any));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "permissions",
        actions: ["manage"],
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 60,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 60,
  },
  {
    accessorKey: "resource",
    header: "Resource",
    size: 60,
    cell: (ctx) => (
      <StatusBadge variant="info">{ctx.getValue<string>()}</StatusBadge>
    ),
  },
  {
    accessorKey: "action",
    header: "Manage",
    size: 60,
    cell: (ctx) => (
      <StatusBadge variant="info">{ctx.getValue<string>()}</StatusBadge>
    ),
  },
] as const satisfies Array<ColumnDef<Permission>>;

function RouteComponent() {
  const searchParams = Route.useSearch();

  const navigate = Route.useNavigate();

  const permissions = useQuery(permissionsListQueryOptions(searchParams));

  const table = useReactTable({
    data: permissions.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
    },
    rowCount: permissions.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const resources = useQuery(permissionResourcesQueryOptions);
  const actions = useQuery(permissionActionsQueryOptions);

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Permissions</h1>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={searchParams.resource ?? "all"}
            onValueChange={(value) => {
              navigate({
                to: ".",
                from: Route.fullPath,
                search: (old) => ({
                  ...old,
                  resource: value === "all" ? undefined : value,
                  page: 1,
                }),
              });
            }}
          >
            <SelectTrigger className="w-auto min-w-[140px]">
              <SelectValue placeholder="All Resources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resources</SelectItem>
              {resources.data.map((resource) => (
                <SelectItem value={resource} key={resource}>
                  {resource}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.action ?? "all"}
            onValueChange={(value) => {
              navigate({
                to: ".",
                from: Route.fullPath,
                search: (old) => ({
                  ...old,
                  action: value === "all" ? undefined : value,
                  page: 1,
                }),
              });
            }}
          >
            <SelectTrigger className="w-auto min-w-[130px]">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actions.data.map((action) => (
                <SelectItem value={action} key={action}>
                  {action}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={
              searchParams.is_active === undefined
                ? "all"
                : String(searchParams.is_active)
            }
            onValueChange={(value: "all" | "true" | "false") => {
              navigate({
                to: ".",
                from: Route.fullPath,
                search: (old) => ({
                  ...old,
                  is_active: value === "all" ? undefined : value === "true",
                  page: 1,
                }),
              });
            }}
          >
            <SelectTrigger className="w-auto min-w-[130px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/5 hover:text-primary"
            onClick={() =>
              navigate({
                to: ".",
                from: Route.fullPath,
                search: (old) => ({
                  ...old,
                  resource: undefined,
                  action: undefined,
                  is_active: undefined,
                }),
              })
            }
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={permissions.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}
